import { useMemo, useState, useRef } from 'react';
import type { ReactNode, ComponentPropsWithoutRef } from 'react';
import { scaleBand, scaleLinear } from '@visx/scale';

import { useDebounce } from '@simplywallst/hooks/useDebounce';

import useWindowSize from '@/hooks/useWindowSize';
import useIsomorphicLayoutEffect from '@/hooks/useIsomorphicLayoutEffect';

import Tooltip from '@/components/Tooltip';

import { Bar } from './Bar';
import { Label } from './Label';

type Bar = {
  id: string;
  label: ReactNode;
  tip: string;
  value: number | null;
};

type Group = Bar[];

interface Props<T extends Group[]> {
  data: T;
  height?: number;
  children: (props: {
    id: T[number][number]['id'];
    value: number | null;
    barProps: ComponentPropsWithoutRef<typeof Bar>;
    labelProps: Omit<ComponentPropsWithoutRef<typeof Label>, 'children'>;
  }) => ReactNode;
}

export function WaterfallChart<T extends Group[]>({
  data,
  children,
  height = 270,
}: Props<T>) {
  const [width, setWidth] = useState(0);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const debouncedSize = useDebounce(useWindowSize(), 500);

  useIsomorphicLayoutEffect(() => {
    const { current } = wrapperRef;

    if (current) {
      const nextWidth = current.getBoundingClientRect().width;
      if (nextWidth > 0) {
        setWidth(nextWidth);
      }
    }
  }, [debouncedSize]);

  const { min, max, keys, bars } = useMemo(() => {
    let min = 0;
    let max = 0;

    data.forEach((group) => {
      group
        .map((n) => n.value ?? 0)
        .reduce((a, b) => {
          const runningTotal = a + b;

          if (runningTotal > max) {
            max = runningTotal;
          }

          if (runningTotal < min) {
            min = runningTotal;
          }

          return a + b;
        }, 0);
    });

    const bars = data.reduce<Bar[]>((acc, group) => {
      group.forEach((bar) => {
        acc.push(bar);
      });
      return acc;
    }, []);

    return { min, max, keys: bars.map((bar) => bar.id), bars };
  }, [data]);

  const xScale = scaleBand({
    domain: keys,
    range: [0, width + 8],
  });

  const yScale = scaleLinear({
    domain: [min, max],
    range: [height - 2, 30],
  });

  return (
    <div>
      <div
        className="w-full overflow-x-hidden"
        style={{ height }}
        ref={wrapperRef}
      >
        <svg
          width={width}
          height={height}
          className="block"
          shapeRendering="crispEdges"
          role="graphics-document"
        >
          {data.map((group, i) => {
            const keys = group.map((bar) => bar.id);
            let runningTotal = 0;
            return (
              <svg
                key={i}
                x={-4}
                y={0}
                width={width + 8}
                height={height}
                shapeRendering="crispEdges"
              >
                {keys.map((n, i) => {
                  const from = runningTotal;
                  const to = runningTotal + (group[i].value ?? 0);

                  runningTotal = to;

                  const y1 = yScale(from);
                  let y2 = yScale(to);

                  // Ensure that the line is at least 1px long
                  if (Math.abs(y1 - y2) < 1) {
                    y2 = y1 + 1;
                  }

                  const x = (xScale(n) || 0) + xScale.bandwidth() / 2;

                  const bandwidth = xScale.bandwidth() - 8;

                  const barProps = {
                    x1: x,
                    y1,
                    x2: x,
                    y2,
                    strokeWidth: bandwidth,
                  };

                  const labelProps = {
                    x: xScale(n) || 0,
                    y: to > from ? yScale(to) : yScale(from),
                    width: bandwidth,
                  };

                  return children({
                    id: n,
                    value: group[i].value,
                    barProps,
                    labelProps,
                  });
                })}
              </svg>
            );
          })}
          <rect
            x={0}
            y={yScale(0)}
            width={width}
            height={1}
            className="fill-subtle"
          />
        </svg>
      </div>
      <div className="grid grid-cols-[repeat(5,1fr)] gap-x1 pt-x2">
        {bars.map((n) => (
          <div key={n.id}>
            <Tooltip
              title={
                <div className="max-w-[260px]">
                  <p className="m-0 text-left text-tiny text-legacy-spacegray">
                    {n.tip}
                  </p>
                </div>
              }
              light
              placement="top"
              zIndex={99999}
            >
              <span className="m-0 border-0 border-b-2 border-dotted border-strong">
                {typeof n.label === 'string' ? (
                  <span className="text-xs font-strong text-soft">
                    {n.label}
                  </span>
                ) : (
                  n.label
                )}
              </span>
            </Tooltip>
          </div>
        ))}
      </div>
    </div>
  );
}

WaterfallChart.Label = Label;
WaterfallChart.Bar = Bar;
